import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, App, AutoComplete, Button, Card, Col, Form, Input, Radio, Row, Segmented, Select, Space, Spin, Steps, Table, Tag, Typography, Upload } from 'antd';
import { CardTitle } from '../../components/StatCard';
import { InboxOutlined } from '@ant-design/icons';
import type { SelectProps } from 'antd';
import { EMAIL_LABELS, IMPORT_CONTACT_MULTI_TARGETS, IMPORT_CONTACT_ORGANIZATION_TARGETS, IMPORT_NEW_FIELD, IMPORT_NEW_FIELD_TYPES, IMPORT_NEW_FIELD_TYPE_LABELS, IMPORT_REPEATABLE_TARGETS, IMPORT_SEGMENTS, ORGANIZATION_FIELD_PREFIX, ORGANIZATION_MATCH_KEYS, PHONE_LABELS, importColumnGroup, importTargetLabel, isImportGroupTarget, suggestColumnLabel, IMPORT_PERSON_KEYS, emptyRuleGroup } from '@crm/shared';
import type { ImportJobItem, ImportNewFieldType, ImportRowItem, ImportTarget, ImportUploadResult } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { errorMessage } from '../../api/client';
import { useCreateList, useCreateSegment, useCreateTag, useLists, useSegments, useTags } from '../../features/audience/api';
import { CreatableSelect } from '../../components/CreatableSelect';
import { useImport, useImportRows, useImportTargets, useSetImportMapping, useStartImport, useUploadImport } from '../../features/imports/api';
import { RecordError } from '../../components/states';
import { plural } from '../../utils/format';

type ObjectType = 'CONTACT' | 'ORGANIZATION';

/** The choices for rows that match a record already in the CRM. */
const duplicateModes = (record: string) => [
  { value: 'merge', title: 'Merge', description: `Fill the existing ${record}'s empty fields; keep every value it already has.` },
  {
    value: 'replace',
    title: 'Replace',
    description: `The file's values overwrite the existing ${record}'s. Empty cells clear nothing${record === 'contact' ? '; the file’s emails, phones and organizations come first, others are kept' : ''}.`,
  },
  { value: 'skip', title: 'Skip', description: `Leave existing ${record}s untouched and only add new ones.` },
];

export function ImportWizardPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const resumeId = params.get('job') ?? undefined;
  const [objectType, setObjectType] = useState<ObjectType>('CONTACT');
  const [uploaded, setUploaded] = useState<ImportUploadResult | null>(null);
  const [mapping, setMapping] = useState<Record<string, string | null>>({});
  /** field type per column mapped to "create a new field", when changed from the suggestion */
  const [newFieldTypes, setNewFieldTypes] = useState<Record<string, ImportNewFieldType>>({});
  /** label per email/phone column, when changed from the one suggested by its header */
  const [columnLabels, setColumnLabels] = useState<Record<string, string>>({});
  const [form] = Form.useForm();

  // resume a job that was uploaded but never started
  const resumed = useImport(resumeId);
  const resumedRows = useImportRows(resumeId, 'all', 1);
  useEffect(() => {
    // wait for the first page of rows too, so the samples and preview are filled in
    if (!resumed.data || !resumedRows.data || uploaded) return;
    if (resumed.data.status !== 'UPLOADED') {
      navigate(`/tools/imports/${resumed.data.id}`, { replace: true });
      return;
    }
    setObjectType(resumed.data.objectType);
    setUploaded({ job: resumed.data, preview: resumedRows.data?.items ?? [], suggestedMapping: resumed.data.mapping?.mapping ?? {} });
    setMapping(resumed.data.mapping?.mapping ?? {});
    setNewFieldTypes(resumed.data.mapping?.options?.newFieldTypes ?? {});
    setColumnLabels(resumed.data.mapping?.options?.columnLabels ?? {});
    if (resumed.data.mapping?.options) form.setFieldsValue(resumed.data.mapping.options);
  }, [resumed.data, resumedRows.data, uploaded, navigate, form]);

  const upload = useUploadImport();
  const setMap = useSetImportMapping();
  const start = useStartImport();
  const targets = useImportTargets(uploaded?.job.objectType ?? objectType);
  const tags = useTags();
  const lists = useLists();
  const segments = useSegments();
  const createTag = useCreateTag();
  const createList = useCreateList();
  const createSegment = useCreateSegment();

  const job: ImportJobItem | undefined = uploaded?.job;
  const used = useMemo(() => new Set(Object.values(mapping).filter(Boolean)), [mapping]);
  const mappedKeys = [...used] as string[];
  const hasPersonKey = IMPORT_PERSON_KEYS.some((k) => mappedKeys.includes(k));
  const orgFieldsUnmatched = mappedKeys.some((k) => k.startsWith(ORGANIZATION_FIELD_PREFIX)) && !ORGANIZATION_MATCH_KEYS.some((k) => mappedKeys.includes(k));
  const requirement = job?.objectType === 'ORGANIZATION' ? mappedKeys.includes('name') : hasPersonKey && !orgFieldsUnmatched;
  const targetByKey = useMemo(() => new Map((targets.data ?? []).map((t) => [t.key, t])), [targets.data]);
  const recordLabel = job?.objectType === 'ORGANIZATION' ? 'organization' : 'contact';
  const typeFor = (header: string): ImportNewFieldType => newFieldTypes[header] ?? job?.columnTypes?.[header] ?? 'TEXT';
  /** Email and phone targets take any number of columns on contact imports, each with a label. */
  const isMulti = (key: string | null | undefined) => job?.objectType === 'CONTACT' && !!key && IMPORT_CONTACT_MULTI_TARGETS.includes(key);
  const labelFor = (header: string, key: string) => columnLabels[header] ?? suggestColumnLabel(header, key) ?? (key === 'mobile' ? 'Mobile' : '');
  /** JSON array items (`emails[1].*`, `companies[2].*`) map as a unit: each item may use its organization fields and labels once. */
  const isContact = job?.objectType === 'CONTACT';
  const takenFor = (header: string, key: string) =>
    Object.entries(mapping).some(([h, k]) => h !== header && k === key && (!isContact || !isImportGroupTarget(key) || importColumnGroup(h) === importColumnGroup(header)));
  /** The column of the same array item that labels this email or phone column, if mapped. */
  const labelColumnFor = (header: string, key: string) => {
    const group = importColumnGroup(header);
    return Object.entries(mapping).find(([h, k]) => k === (key === 'email' ? 'email_label' : 'phone_label') && importColumnGroup(h) === group)?.[0];
  };
  const hasArrayColumns = isContact && (job?.headers ?? []).some((h) => importColumnGroup(h));
  /** What a column is imported into, in words (picker, preview). */
  const targetLabel = (header: string, key: string) => {
    if (key === IMPORT_NEW_FIELD) return `New field · ${IMPORT_NEW_FIELD_TYPE_LABELS[typeFor(header)]}`;
    if (key === IMPORT_SEGMENTS) return 'Segments (one per value)';
    const t = targetByKey.get(key);
    const labelColumn = isMulti(key) ? labelColumnFor(header, key) : undefined;
    if (t && labelColumn) return `${key === 'mobile' ? 'Phone' : t.label} · label from ${labelColumn}`;
    if (t && isMulti(key) && labelFor(header, key)) return `${key === 'mobile' ? 'Phone' : t.label} · ${labelFor(header, key)}`;
    return t ? importTargetLabel(t) : key;
  };
  /** Field picker options for one column: create actions, then the record's fields (and organization fields for contacts). */
  const targetOptions = (header: string, current: string | null | undefined): SelectProps['options'] => {
    const option = (t: ImportTarget) => ({ value: t.key, label: t.label, disabled: t.key !== current && takenFor(header, t.key) && !IMPORT_REPEATABLE_TARGETS.includes(t.key) && !isMulti(t.key) && !(isContact && IMPORT_CONTACT_ORGANIZATION_TARGETS.includes(t.key)) });
    const all = targets.data ?? [];
    const own = all.filter((t) => t.group === 'record').map(option);
    const org = all.filter((t) => t.group === 'organization').map(option);
    const create = [
      { value: IMPORT_NEW_FIELD, label: `Create new ${recordLabel} field “${header}”` },
      ...(job?.objectType === 'CONTACT' ? [{ value: IMPORT_SEGMENTS, label: 'Segments: one per value, contact added to each' }] : []),
    ];
    return [
      { label: 'Create', title: 'Create', options: create },
      { label: job?.objectType === 'ORGANIZATION' ? 'Organization' : 'Contact', title: 'Fields', options: own },
      ...(org.length ? [{ label: 'Organization', title: 'Organization', options: org }] : []),
    ];
  };
  const segmentColumns = Object.entries(mapping).filter(([, k]) => k === IMPORT_SEGMENTS).map(([h]) => h);
  const [uploadPercent, setUploadPercent] = useState(0);
  const preview = uploaded?.preview ?? [];
  const sample = (header: string) => preview.map((r) => r.data[header]).filter(Boolean).slice(0, 3);

  const onFile = async (file: File) => {
    try {
      setUploadPercent(0);
      const res = await upload.mutateAsync({ file, objectType, onProgress: setUploadPercent });
      setUploaded(res);
      setMapping(res.suggestedMapping);
      const n = Object.values(res.suggestedMapping).filter(Boolean).length;
      message.success(`${plural(res.job.totalRows, 'row')} read. ${n} of ${res.job.headers.length} columns matched automatically.`);
    } catch (e) {
      message.error(errorMessage(e));
    }
    return false;
  };

  const onStart = async () => {
    if (!job) return;
    try {
      const options = form.getFieldsValue();
      const types = Object.fromEntries(Object.entries(mapping).filter(([, k]) => k === IMPORT_NEW_FIELD).map(([h]) => [h, typeFor(h)]));
      const labels = Object.fromEntries(Object.entries(mapping).filter(([, k]) => isMulti(k)).map(([h, k]) => [h, labelFor(h, k!)]));
      await setMap.mutateAsync({
        id: job.id,
        mapping,
        options: { onDuplicate: options.onDuplicate ?? 'merge', tagIds: options.tagIds ?? [], listId: options.listId ?? null, segmentId: options.segmentId ?? null, source: options.source || undefined, newFieldTypes: types, columnLabels: labels },
      });
      await start.mutateAsync(job.id);
      message.success('Import started');
      navigate(`/tools/imports/${job.id}`);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const step = uploaded ? 1 : 0;
  const resuming = !!resumeId && !uploaded;
  const resumeError = resumed.error ?? resumedRows.error;
  if (resuming && resumeError) {
    return <RecordError error={resumeError} what="Import" backTo="/tools/imports" backLabel="All imports" onRetry={() => { void resumed.refetch(); void resumedRows.refetch(); }} />;
  }
  return (
    <div className="page">
      <PageHeader
        breadcrumb={[{ title: 'Importer', to: '/tools/imports' }, { title: 'New import' }]}
        title="New import"
        subtitle="Upload a file, map its columns to fields, choose how duplicates are handled, then start."
      />
      <Card style={{ marginBottom: 24 }}>
        <Steps current={step} items={[{ title: 'Upload a file', description: 'CSV, XLSX or JSON' }, { title: 'Map columns and start', description: 'Match columns to fields' }]} />
      </Card>

      {resuming && (
        <Card>
          <div style={{ textAlign: 'center', padding: 32 }}><Spin tip="Loading the uploaded file…"><div style={{ height: 24 }} /></Spin></div>
        </Card>
      )}

      {!uploaded && !resuming && (
        <Card title={<CardTitle title="Upload a file" subtitle="The first row must contain column headers" />}>
          <Space orientation="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Typography.Text strong>What are you importing?</Typography.Text>
              <div style={{ marginTop: 8 }}>
                <Segmented value={objectType} onChange={(v) => setObjectType(v as typeof objectType)} options={[{ value: 'CONTACT', label: 'Contacts' }, { value: 'ORGANIZATION', label: 'Organizations' }]} />
              </div>
            </div>
            <Upload.Dragger accept=".csv,.xlsx,.xlsm,.json,.jsonl,.ndjson,text/csv,application/json,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" multiple={false} showUploadList={false} beforeUpload={(f) => onFile(f as unknown as File)} disabled={upload.isPending}>
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p className="ant-upload-text">{!upload.isPending ? 'Click or drag a .csv, .xlsx or .json file here' : uploadPercent < 100 ? `Uploading… ${uploadPercent}%` : 'Reading rows… large files take up to a minute'}</p>
              <p className="ant-upload-hint">The first row must contain column headers. XLSX files use the first sheet. JSON files hold a list of records; nested lists (emails, phones, companies) get a column per item. Up to 25 MB and 50,000 rows.</p>
            </Upload.Dragger>
          </Space>
        </Card>
      )}

      {uploaded && job && (
        <Space orientation="vertical" size="large" style={{ width: '100%' }}>
          <Card title={<CardTitle title={<span>{job.filename} <Tag color="processing" style={{ marginLeft: 8, verticalAlign: 'middle' }}>{job.objectType === 'CONTACT' ? 'Contacts' : 'Organizations'}</Tag></span>} subtitle={`${plural(job.totalRows, 'row')} · ${plural(job.headers.length, 'column')}. Choose a field for each column, or skip it.`} />} extra={<Button onClick={() => { setUploaded(null); setMapping({}); navigate('/tools/imports/new', { replace: true }); }}>Choose another file</Button>}>
            {!requirement && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                title={
                  job.objectType === 'ORGANIZATION'
                    ? 'Map the organization Name column to continue.'
                    : !hasPersonKey
                      ? 'Map at least an Email, First name, Last name or Full name column to continue.'
                      : 'You mapped organization fields: also map the organization Name, Domain or Website column so each contact can be linked to its organization.'
                }
              />
            )}
            {segmentColumns.length > 0 && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                title={`Each value in ${segmentColumns.map((h) => `“${h}”`).join(', ')} becomes a segment, and the contact is added to it.`}
                description="The values are also saved in a list field named after the column, which the segment's rule matches. A segment that already exists under the same name keeps its rules and also includes these contacts."
              />
            )}
            {hasArrayColumns && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                title="Lists in the file have a column per item, like emails[2].email or companies[1].website."
                description="Columns of the same item are imported together: each email or phone gets the label in its item's label column, and each company becomes its own organization, linked to the contact with its own fields, logo and primary flag. The primary company's job title fills the contact's job title."
              />
            )}
            {job.objectType === 'CONTACT' && requirement && (
              <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
                Columns about the company (name, industry, employees, LinkedIn…) can go to <b>Organization</b> fields. Each contact is linked to the organization in its row, which is found by domain or name, or created. Existing organizations only get their empty fields filled.
                {' '}Several columns can go to <b>Email</b> or <b>Phone</b>, each with its own label (Work, Personal, Mobile…); the first email and phone filled in a row become the primary ones.
              </Typography.Paragraph>
            )}
            <Table<{ header: string }>
              rowKey="header"
              tableLayout="fixed"
              pagination={false}
              dataSource={job.headers.map((header) => ({ header }))}
              columns={[
                { title: 'Column in file', dataIndex: 'header', width: 240, render: (h: string) => <Typography.Text strong>{h}</Typography.Text> },
                {
                  title: 'Sample values',
                  key: 'sample',
                  render: (_, r) => <Typography.Text type="secondary" ellipsis style={{ maxWidth: '100%', display: 'inline-block' }}>{sample(r.header).join(' · ') || <i>Empty</i>}</Typography.Text>,
                },
                {
                  title: 'Import into',
                  key: 'target',
                  width: 320,
                  render: (_, r) => (
                    <Space orientation="vertical" size={6} style={{ width: '100%' }}>
                      <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="Skip this column"
                        value={mapping[r.header] ?? undefined}
                        onChange={(v) => setMapping((m) => ({ ...m, [r.header]: (v as string | undefined) ?? null }))}
                        style={{ width: '100%' }}
                        loading={targets.isLoading}
                        options={targetOptions(r.header, mapping[r.header])}
                        labelRender={({ value }) => targetLabel(r.header, value as string)}
                      />
                      {isMulti(mapping[r.header]) && labelColumnFor(r.header, mapping[r.header]!) && (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>Label from “{labelColumnFor(r.header, mapping[r.header]!)}” in each row</Typography.Text>
                      )}
                      {isMulti(mapping[r.header]) && !labelColumnFor(r.header, mapping[r.header]!) && (
                        <AutoComplete
                          size="small"
                          aria-label={`Label for ${r.header}`}
                          placeholder="Label, e.g. Work"
                          value={labelFor(r.header, mapping[r.header]!)}
                          onChange={(v: string) => setColumnLabels((l) => ({ ...l, [r.header]: v }))}
                          style={{ width: '100%' }}
                          maxLength={40}
                          options={(mapping[r.header] === 'email' ? EMAIL_LABELS : PHONE_LABELS).map((l) => ({ value: l }))}
                          filterOption={false}
                        />
                      )}
                      {mapping[r.header] === IMPORT_NEW_FIELD && (
                        <Select
                          size="small"
                          aria-label={`Field type for ${r.header}`}
                          value={typeFor(r.header)}
                          onChange={(v) => setNewFieldTypes((t) => ({ ...t, [r.header]: v }))}
                          style={{ width: '100%' }}
                          options={IMPORT_NEW_FIELD_TYPES.map((t) => ({ value: t, label: `Type: ${IMPORT_NEW_FIELD_TYPE_LABELS[t]}${t === job.columnTypes?.[r.header] ? ' (suggested)' : ''}` }))}
                        />
                      )}
                    </Space>
                  ),
                },
              ]}
            />
          </Card>

          <Card title={<CardTitle title="Preview" subtitle="The first rows as they appear in the file, with the field each column goes to" />}>
            <Table<ImportRowItem>
              rowKey="rowNumber"
              scroll={{ x: 'max-content' }}
              pagination={false}
              dataSource={preview.slice(0, 10)}
              columns={[{ title: '#', dataIndex: 'rowNumber', width: 60, fixed: 'left' }, ...job.headers.map((h) => ({ title: <span>{h}{mapping[h] ? <div><Tag color={mapping[h] === IMPORT_NEW_FIELD || mapping[h] === IMPORT_SEGMENTS ? 'green' : targetByKey.get(mapping[h]!)?.group === 'organization' ? 'purple' : 'processing'} style={{ marginTop: 4 }}>{targetLabel(h, mapping[h]!)}</Tag></div> : <div><Typography.Text type="secondary" style={{ fontSize: 11 }}>Skipped</Typography.Text></div>}</span>, key: h, ellipsis: true, render: (_: unknown, r: ImportRowItem) => r.data[h] }))]}
            />
          </Card>

          <Card title={<CardTitle title="Options" subtitle="Duplicate handling and what to apply to every imported record" />}>
            <Form form={form} layout="vertical" initialValues={{ onDuplicate: 'merge', tagIds: [], listId: null, segmentId: null, source: '' }}>
              <Form.Item
                name="onDuplicate"
                label={job.objectType === 'CONTACT' ? 'Existing contacts (a row whose email already belongs to a contact)' : 'Existing organizations (a row matching one by domain, then name)'}
              >
                <Radio.Group style={{ width: '100%' }}>
                  <Row gutter={[12, 12]}>
                    {duplicateModes(recordLabel).map((m) => (
                      <Col xs={24} md={8} key={m.value}>
                        <Radio value={m.value}><Typography.Text strong>{m.title}</Typography.Text></Radio>
                        <div style={{ paddingLeft: 24 }}><Typography.Text type="secondary" style={{ fontSize: 12 }}>{m.description}</Typography.Text></div>
                      </Col>
                    ))}
                  </Row>
                </Radio.Group>
              </Form.Item>
              {job.objectType === 'CONTACT' && (
                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item name="source" label="Lead source for new contacts" tooltip="Used when the file has no mapped Source column. Defaults to “Import”.">
                      <Input placeholder="e.g. Trade show 2026" maxLength={60} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="tagIds" label="Add these tags to every imported contact">
                      <CreatableSelect multiple items={tags.data} placeholder="Optional · type to create a tag" create={{ noun: 'tag', pending: createTag.isPending, run: async (name) => (await createTag.mutateAsync({ name })).id }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="listId" label="Add every imported contact to a list">
                      <CreatableSelect items={lists.data} placeholder="Optional · type to create a list" create={{ noun: 'list', pending: createList.isPending, run: async (name) => (await createList.mutateAsync({ name })).id }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="segmentId" label="Add every imported contact to a segment" tooltip="The contacts are added by hand, so they stay in the segment even if they don't match its rules.">
                      <CreatableSelect items={segments.data} placeholder="Optional · type to create a segment" create={{ noun: 'segment', pending: createSegment.isPending, run: async (name) => (await createSegment.mutateAsync({ name, rules: emptyRuleGroup() })).id }} />
                    </Form.Item>
                  </Col>
                </Row>
              )}
              {job.objectType === 'CONTACT' && <Typography.Text type="secondary">New contacts get the built-in email checks (syntax, disposable, role, DNS) when those are enabled under Admin → Verification.</Typography.Text>}
            </Form>
          </Card>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
            <Typography.Text type="secondary">Runs in the background on the worker. You can watch progress on the next screen.</Typography.Text>
            <Button type="primary" size="large" disabled={!requirement} loading={setMap.isPending || start.isPending} onClick={onStart}>
              Start import of {plural(job.totalRows, 'row')}
            </Button>
          </div>
        </Space>
      )}
    </div>
  );
}
